import { memo, useCallback, useState } from "react";

import Link from "next/link";

import {
  Button,
  InfoModal,
  Checkbox,
  Toast,
  LoadingSpinner,
} from "@newsela/angelou";
import { useRouter } from "next/router";
import PropTypes from "prop-types";
import ReactDOM from "react-dom";

import { actionNames, objectTypes, withTrackEvent } from "@newsela/snitch";
import { CONTENT_SETTINGS_TYPE } from "@/assignments/constants/contentSettings.js";
import useAddToAssignment from "@/assignments/useAddToAssignment/useAddToAssignment";
import useAssignments from "@/assignments/useAssignmentsGet";
import {
  determineSRCState,
  getConSetVisibilityMarkers,
} from "@/assignments/utils/assignmentUtils";
import globalToasterRef from "@/base/globalToasterRef/globalToasterRef.js";
import { ContentAdvisoryConsentMessage } from "@/components/ContentAdvisoryConsentMessage/ContentAdvisoryConsentMessage.js";
import useContent from "@/content/useContent";
import useAdvisoryMessageGet from "@/district/hooks/useAdvisoryMessageGet/useAdvisoryMessageGet.js";
import { MONOLITH_ORIGIN } from "@/monolithIntegration/monolithIntegration.constants";
import AssignmentRow from "./AssignModalRow";
import { assignmentsWithActiveAssigneesOrAreDrafts } from "./AssignModal.utils.js";
import {
  assignmentListCss,
  advisoryCheckboxCss,
  loadingWrapper,
  modalCss,
  modalInstructions,
  toastWrapper,
  modalFooter,
  modalWarning,
  separatorCss,
} from "./AssignModal.style.js";

const ButtonWithTrackEvent = withTrackEvent(Button);
const LinkWithTrackEvent = withTrackEvent(Link);

export const isAddDisabled = (
  selectedCount,
  shouldDisplayContentAdvisory,
  acceptedContentAdvisory,
) =>
  (shouldDisplayContentAdvisory && !acceptedContentAdvisory) ||
  selectedCount <= 0;

const propTypes = {
  assignmentsDerivedFromViewPage: PropTypes.arrayOf(PropTypes.object),
  createAssignmentUrl: PropTypes.string,
  contentId: PropTypes.string,
  contentMemberIds: PropTypes.arrayOf(PropTypes.string),
  modalProps: PropTypes.object,
  handleClickToCloseModal: PropTypes.func,
  /* recommendation_set_ids derived from the V3 Content API */
  recommendationSetIds: PropTypes.arrayOf(PropTypes.string),
};

const AssignModal = ({
  assignmentsDerivedFromViewPage,
  createAssignmentUrl,
  contentMemberIds,
  modalProps,
  handleClickToCloseModal,
  recommendationSetIds,
}) => {
  const { data: assignments, loading: loadingAssignments } = useAssignments();
  const router = useRouter();
  const [addToAssignment] = useAddToAssignment();
  const [selectedAssignments, setSelectedAssignments] = useState({});
  const [updatePending, setUpdatePending] = useState(false);
  const [acceptedContentAdvisory, setAcceptedContentAdvisory] = useState(false);
  const { data: catProps } = useAdvisoryMessageGet();

  const { data: newContent } = useContent(contentMemberIds, {
    depth: 1,
    shape: "card",
    use_imgix: true,
  });

  const containsActiveAssignment = Object.values(selectedAssignments).some(
    (assignment) => !assignment.is_draft,
  );

  const contentWithAdvisory = (newContent || []).filter(
    (content) => content.veil?.reason === "DISTRICT_ADVISORY",
  );
  const conSetVisibilityMarkers = getConSetVisibilityMarkers(newContent || []);

  const ContentIdsWithAdvisory = contentWithAdvisory.reduce((acc, content) => {
    acc.push(content.content_id);
    return acc;
  }, []);

  const getTaggedContentType = () => {
    if (contentWithAdvisory.length) {
      return CONTENT_SETTINGS_TYPE.CAT;
    }
    const { containsSRC } = determineSRCState(newContent || []);
    if (containsSRC) {
      return CONTENT_SETTINGS_TYPE.SRC;
    }
    return null;
  };

  const taggedContentType = getTaggedContentType();

  const shouldDisplayContentAdvisory =
    containsActiveAssignment && taggedContentType;

  const assignmentsArray = assignmentsWithActiveAssigneesOrAreDrafts(
    assignments || assignmentsDerivedFromViewPage,
  );

  const contentListIds = (newContent || [])
    .reduce((acc, content) => {
      acc.push(content.content_id);
      return acc;
    }, [])
    .join();

  const navigateToCreateAssignment = () => {
    router.push(createAssignmentUrl);
    handleClickToCloseModal();
  };

  const addAssignment = (assignment) => {
    const addedToList = { ...selectedAssignments };
    addedToList[assignment.content_id] = assignment;
    setSelectedAssignments(addedToList);
  };
  const removeAssignment = (assignment) => {
    const removedFromList = { ...selectedAssignments };
    delete removedFromList[assignment.content_id];
    setSelectedAssignments(removedFromList);
  };

  const handleAddClick = useCallback(() => {
    setUpdatePending(true);
    const arrayOfAssignments = Object.values(selectedAssignments);
    const requests = arrayOfAssignments.map((assignment) =>
      addToAssignment({
        assignment,
        contentMemberIds,
        districtAdvisoryOverride: ContentIdsWithAdvisory.join(),
        contentSettingsAdvisoryOverride: contentListIds,
      }),
    );
    Promise.all(requests)
      .then((responses) => {
        let error;
        responses.forEach((response) => {
          error = response?.error;
        });
        if (error) {
          throw new Error(error);
        }
        globalToasterRef.current.displayToast({
          statusColor: Toast.statusColor.success,
          children: (
            <span css={toastWrapper} data-qa-selector="success_message">
              Content Added
              <LinkWithTrackEvent
                href={`${MONOLITH_ORIGIN}/binder/assignments`}
                legacyBehavior
                eventName="navigate-from-add-article-success"
              >
                View Assignments
              </LinkWithTrackEvent>
            </span>
          ),
        });
        handleClickToCloseModal();
      })
      .catch(() => {
        globalToasterRef.current.displayToast({
          statusColor: Toast.statusColor.danger,
          children:
            "Your assignment(s) failed to update. Please try again later",
        });
        handleClickToCloseModal();
      });
  }, [selectedAssignments, contentListIds]);

  const UpdateAssignmentModalFooter = () => {
    const selectedCount = Object.keys(selectedAssignments).length;
    const addLabel =
      selectedCount > 1
        ? `Add to ${selectedCount} Assignments`
        : "Add to Assignment";
    return (
      <>
        {shouldDisplayContentAdvisory ? (
          <div css={modalWarning({ updatePending })} id="modalwarning">
            <div>{catProps.title}</div>
            <ContentAdvisoryConsentMessage
              {...catProps}
              conSetVisibilityMarkers={conSetVisibilityMarkers}
            />

            <Checkbox
              label="I’ve read this message and choose to assign the content."
              __cssFor={advisoryCheckboxCss({ acceptedContentAdvisory })}
              checked={acceptedContentAdvisory}
              onClick={() => {
                setAcceptedContentAdvisory(!acceptedContentAdvisory);
              }}
            />
          </div>
        ) : (
          <></>
        )}
        <div css={modalFooter({ updatePending })} id="modalfooter">
          <ButtonWithTrackEvent
            /* AUTOGENERATED TODO: update angelou to new flavor.
          see https://github.com/newsela/angelou/blob/main/src/components/Button/README.md#MIGRATION
          for migration guide. */
            legacy_flavor={Button.legacy_flavor.solid}
            legacy_size={Button.legacy_size.medium}
            legacy_statusColor={Button.legacy_statusColor.tertiary}
            onClick={handleClickToCloseModal}
            objectType={objectTypes.assignment}
            actionPrefix="Update"
            actionName={actionNames.cancel}
            streamProperties={{
              description: "Close Update Assignment modal",
              recommendation_set_ids: recommendationSetIds,
              ...(shouldDisplayContentAdvisory && {
                component_type: "content-advisory-assignment",
                content_id: contentMemberIds,
                event_name: "Cancel",
                content_advisory_override_consent: acceptedContentAdvisory,
              }),
            }}
          >
            Cancel
          </ButtonWithTrackEvent>
          <ButtonWithTrackEvent
            disabled={isAddDisabled(
              selectedCount,
              shouldDisplayContentAdvisory,
              acceptedContentAdvisory,
            )}
            /* AUTOGENERATED TODO: update angelou to new flavor.
          see https://github.com/newsela/angelou/blob/main/src/components/Button/README.md#MIGRATION
          for migration guide. */
            legacy_flavor={Button.legacy_flavor.solid}
            legacy_size={Button.legacy_size.medium}
            legacy_statusColor={Button.legacy_statusColor.primary}
            onClick={handleAddClick}
            objectType={objectTypes.assignment}
            actionPrefix="Update"
            actionName={actionNames.add}
            streamProperties={{
              description: "Update Assignment",
              recommendation_set_ids: recommendationSetIds,
              ...(shouldDisplayContentAdvisory && {
                component_type: "content-advisory-assignment",
                content_id: contentMemberIds,
                event_name: "Consent Event",
                content_advisory_override_consent: acceptedContentAdvisory,
              }),
            }}
          >
            {addLabel}
          </ButtonWithTrackEvent>
        </div>
      </>
    );
  };

  const CreateAssignmentModalFooter = () => {
    const qaSelectorPrefix = shouldDisplayContentAdvisory ? "cat_" : "";
    return (
      <div css={modalFooter} id="modalfooter">
        <ButtonWithTrackEvent
          /* AUTOGENERATED TODO: update angelou to new flavor.
            see https://github.com/newsela/angelou/blob/main/src/components/Button/README.md#MIGRATION
            for migration guide. */
          legacy_flavor={Button.legacy_flavor.solid}
          legacy_size={Button.legacy_size.medium}
          legacy_statusColor={Button.legacy_statusColor.tertiary}
          onClick={handleClickToCloseModal}
          objectType={objectTypes.assignment}
          actionPrefix="Create"
          actionName={actionNames.cancel}
          dataProps={{
            "data-qa-selector": `${qaSelectorPrefix}_close_create_assignment_modal`,
          }}
          streamProperties={{
            description: "Close Create Assignment modal",
            recommendation_set_ids: recommendationSetIds,
            ...(shouldDisplayContentAdvisory && {
              component_type: "content-advisory-assignment",
              content_id: contentMemberIds,
              event_name: "Cancel",
              content_advisory_override_consent: acceptedContentAdvisory,
            }),
          }}
        >
          Cancel
        </ButtonWithTrackEvent>
        <ButtonWithTrackEvent
          /* AUTOGENERATED TODO: update angelou to new flavor.
            see https://github.com/newsela/angelou/blob/main/src/components/Button/README.md#MIGRATION
            for migration guide. */
          legacy_flavor={Button.legacy_flavor.solid}
          legacy_size={Button.legacy_size.medium}
          legacy_statusColor={Button.legacy_statusColor.primary}
          onClick={() => navigateToCreateAssignment()}
          objectType={objectTypes.assignment}
          actionPrefix="Create"
          actionName={actionNames.add}
          dataProps={{
            "data-qa-selector": `${qaSelectorPrefix}_create_assignment_btn_modal`,
          }}
          streamProperties={{
            description: "Create Assignment",
            recommendation_set_ids: recommendationSetIds,
          }}
        >
          Create Assignment
        </ButtonWithTrackEvent>
      </div>
    );
  };

  const handleClick = (assignment) => {
    if (selectedAssignments[assignment.content_id]) {
      removeAssignment(assignment);
    } else {
      addAssignment(assignment);
    }
  };

  const hasAssignments =
    Array.isArray(assignmentsArray) && assignmentsArray.length;

  let modalContent;
  if (updatePending || loadingAssignments) {
    modalContent = (
      <div css={loadingWrapper}>
        <LoadingSpinner />
      </div>
    );
  } else if (hasAssignments) {
    modalContent = (
      <>
        <p css={modalInstructions}>
          Add this content to one or more of your existing assignments.
        </p>
        <hr css={separatorCss} />
        <ul css={assignmentListCss}>
          {/* update to map through Assignments returned from the api with content id as key */}
          {assignmentsArray.map((assignment, index) => (
            <AssignmentRow
              alreadySelected={selectedAssignments}
              key={assignment.contentId}
              assignment={assignment}
              isLastItem={assignmentsArray.length - 1 === index}
              handleClick={handleClick}
            />
          ))}
        </ul>
      </>
    );
  } else {
    modalContent = (
      <p css={modalInstructions} data-qa-selector="modal_instructions">
        You currently have no assignments. Would you like to create one?
      </p>
    );
  }

  const footer = hasAssignments ? (
    <UpdateAssignmentModalFooter />
  ) : (
    <CreateAssignmentModalFooter />
  );

  const modal = (
    <InfoModal
      __cssFor={modalCss({ updatePending })}
      {...modalProps}
      labelId="UpdateAssignmentModal"
      onBackgroundClick={handleClickToCloseModal}
      closeButtonProps={{
        onClick: () => handleClickToCloseModal(),
      }}
      title="Add content to Assignment"
      footer={footer}
    >
      {modalContent}
    </InfoModal>
  );

  const modalRoot = document.getElementById("modal-root");
  return ReactDOM.createPortal(modal, modalRoot);
};

AssignModal.propTypes = propTypes;

export default memo(AssignModal);
